/**
 * The photograph. Required to submit, public once stored, and destructive to replace.
 *
 * REPLACING AN AVATAR DESTROYS BOTH PREVIOUS COPIES — the public rendition and the private master
 * — in the same transaction. One author, one avatar, always. So replacing is behind a confirm
 * whose initial focus is on Cancel, never on the destructive choice.
 *
 * `accept` LISTS JPEG, PNG AND WEBP EXPLICITLY and never a bundled "all images" constant, because
 * SVG IS REJECTED SERVER-SIDE by magic bytes. A control that lets somebody drop an SVG only for
 * the claim to fail afterwards has misled them. And because validation is by magic bytes rather
 * than by extension, the picker's filter is a courtesy and never a guarantee — the wording says so
 * rather than promising a check we do not perform here.
 *
 * NOTHING IS UPLOADED TO US. The browser writes the bytes into its own Firebase Storage quarantine
 * prefix and then calls `claimAvatarUpload` to name the object. The drop target below is a picker
 * and performs no request of its own.
 *
 * ★ THE PROTOTYPE'S PORTRAIT CARD (the owner's live look at real cabinets, 2026-10-01 / design 15):
 * the photograph large, a line saying where readers meet it, the gold button, and - once there is
 * one - the same picture at the smaller sizes it is shown at beside a name (an article's byline,
 * an entry in a list; docs/audit/domains/author-profile.md, "author bylines on every content
 * surface"). The previews are decoration and hidden from the accessibility tree: they repeat the
 * photograph above them. The buttons' words, "Choose a profile photo" and "Choose a different
 * profile photo" - web/ui/test/e2e/cabinet-dialogs-accessibility.spec.ts presses them by
 * name. The whole row still takes a dropped file.
 */
import { useRef, useState } from 'react';
import type { DragEvent, ReactNode } from 'react';
import { Avatar, Button, Group, Modal, Progress, Stack, Text } from '@mantine/core';
import { ImagePlus } from '@moonx/ui';
import { useTranslation } from '../../../i18n';
import { AVATAR_ACCEPT, MAX_UPLOAD_BYTES } from '../domain/limits';
import { humanMessage } from '../api/problem';
import type { AvatarUpload } from '../data/useUpload';
import { useServedImageSrc } from '../../storage/emulator-image-url';
import classes from './profile.module.css';

export interface AvatarFieldProps {
  readonly id: string;
  readonly avatarUrl: string | null | undefined;
  readonly displayName: string;
  readonly upload: AvatarUpload;
  readonly disabled?: boolean;
}

export function AvatarField({
  id,
  avatarUrl,
  displayName,
  upload,
  disabled,
}: AvatarFieldProps): ReactNode {
  const { t } = useTranslation('application');
  const inputRef = useRef<HTMLInputElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [dragging, setDragging] = useState(false);
  const [pendingReplacement, setPendingReplacement] = useState<File | null>(null);

  const hasAvatar = typeof avatarUrl === 'string' && avatarUrl.length > 0;
  const busy = upload.phase === 'writing' || upload.phase === 'claiming';
  // Through `useServedImageSrc`: the API's URL is production Storage's download address, which the
  // local stack serves only for what it wrote; production builds get it back unchanged.
  const served = useServedImageSrc(hasAvatar ? avatarUrl : null);
  const shown = served.src;

  function choose(file: File | undefined): void {
    if (file === undefined) return;
    // Replacing destroys the previous file irreversibly, so it asks first. A first upload does not,
    // and neither does a file too large to upload at all: asking "replace it?" about a file that is
    // then refused is a question with no real answer. The hook refuses it before a byte moves and
    // says why (`useAvatarUpload`, ../data/useUpload.ts), and the current photograph is untouched.
    if (hasAvatar && file.size < MAX_UPLOAD_BYTES) setPendingReplacement(file);
    else void upload.upload(file);
  }

  function onDrop(event: DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    setDragging(false);
    if (disabled === true || busy) return;
    choose(event.dataTransfer.files[0]);
  }

  return (
    <Stack gap="sm" id={id}>
      <div
        className={classes.portrait}
        data-dragging={dragging ? '' : undefined}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => {
          setDragging(false);
        }}
        onDrop={onDrop}
      >
        <Avatar
          src={shown}
          imageProps={{ onError: served.onError }}
          size={96}
          radius="xl"
          alt=""
          className={classes.portraitPhoto}
        >
          {displayName.slice(0, 1).toUpperCase() || '?'}
        </Avatar>

        <div className={classes.portraitText}>
          <p className={classes.portraitLead}>{t('avatar.intro')}</p>
          <input
            ref={inputRef}
            type="file"
            accept={AVATAR_ACCEPT.join(',')}
            hidden
            onChange={(event) => {
              choose(event.currentTarget.files?.[0]);
              // Clearing lets the same file be chosen twice in a row after a failure.
              event.currentTarget.value = '';
            }}
          />
          <Button
            radius="xl"
            leftSection={<ImagePlus size={15} aria-hidden="true" />}
            loading={busy}
            disabled={disabled === true}
            onClick={() => inputRef.current?.click()}
          >
            {hasAvatar ? t('avatar.chooseDifferent') : t('avatar.choose')}
          </Button>

          {upload.phase === 'writing' ? (
            <Stack gap={4} style={{ width: '100%' }}>
              <Progress
                value={Math.round(upload.progress * 100)}
                size="xs"
                aria-label={t('avatar.uploadingLabel')}
              />
              <Text size="xs" c="dimmed">
                {t('upload.uploading', { percent: Math.round(upload.progress * 100) })}
              </Text>
            </Stack>
          ) : null}
          {upload.phase === 'claiming' ? (
            <Text size="xs" c="dimmed" aria-live="polite">
              {t('avatar.checking')}
            </Text>
          ) : null}
          {upload.phase === 'done' ? (
            <Text size="xs" aria-live="polite" style={{ color: 'oklch(var(--mx-success))' }}>
              {t('avatar.saved')}
            </Text>
          ) : null}
          {upload.phase === 'failed' ? (
            <Group gap="xs" role="alert">
              <Text size="xs" style={{ color: 'oklch(var(--mx-danger))' }}>
                {humanMessage(upload.error, t('avatar.failed'), 'avatar', t)}
              </Text>
              <Button size="compact-xs" variant="subtle" onClick={upload.reset}>
                {t('upload.tryAnother')}
              </Button>
            </Group>
          ) : null}
        </div>

        {shown === null ? null : (
          <div className={classes.previews} aria-hidden="true">
            <span className={classes.preview}>
              <img src={shown} onError={served.onError} alt="" width={44} height={44} />
              {t('avatar.preview.byline')}
            </span>
            <span className={classes.preview}>
              <img src={shown} onError={served.onError} alt="" width={26} height={26} />
              {t('avatar.preview.lists')}
            </span>
          </div>
        )}
      </div>

      <Modal
        opened={pendingReplacement !== null}
        onClose={() => {
          setPendingReplacement(null);
        }}
        // Required: it supplies the dialog's accessible name.
        title={t('avatar.replace.title')}
        centered
        // Initial focus on Cancel, never on the destructive choice.
        onEnterTransitionEnd={() => cancelRef.current?.focus()}
      >
        <Stack gap="md">
          <Text size="sm">{t('avatar.replace.body')}</Text>
          <Group justify="flex-end">
            <Button
              ref={cancelRef}
              variant="default"
              onClick={() => {
                setPendingReplacement(null);
              }}
            >
              {t('avatar.replace.keep')}
            </Button>
            <Button
              onClick={() => {
                const file = pendingReplacement;
                setPendingReplacement(null);
                if (file !== null) void upload.upload(file);
              }}
            >
              {t('avatar.replace.confirm')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
